// @ts-nocheck
// ============================================
// 标注统计面板组件
// 功能：显示标注统计信息、活跃页面分析
// ============================================

import React, { useState, useEffect } from 'react';
import { getAnnotationStatistics } from './pdfUtils';
import './StatisticsPanel.css';

interface Statistics {
  total_annotations: number;
  guide_count: number;
  qa_count: number;
  manual_count: number;
  sticky_note_count: number;
  deleted_count: number;
  annotated_pages: number;
  last_annotation_time: string | null;
  by_type: Record<string, number>;
}

interface StatisticsPanelProps {
  show: boolean;
  pdfFingerprint: string;
  onClose: () => void;
}

const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ show, pdfFingerprint, onClose }) => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && pdfFingerprint) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pdfFingerprint]);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getAnnotationStatistics(pdfFingerprint) as any;

      if (result.success) {
        setStatistics({
          total_annotations: result.total || 0,
          guide_count: result.qa_count || 0,
          qa_count: result.by_type?.qa_normal || 0,
          manual_count: result.annotation_count || 0,
          sticky_note_count: result.sticky_note_count || 0,
          deleted_count: 0,
          annotated_pages: result.annotated_pages || 0,
          last_annotation_time: result.last_annotation_time || null,
          by_type: result.by_type || {}
        });
      } else {
        throw new Error(result.error || '获取统计数据失败');
      }
    } catch (err: any) {
      console.error('获取统计数据失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '暂无';

    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 60000) {
      return '刚刚';
    }

    if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}分钟前`;
    }

    if (diff < 86400000) {
      return `${Math.floor(diff / 3600000)}小时前`;
    }

    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPercentage = (value: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  if (!show) return null;

  return (
    <div className="statistics-overlay" onClick={onClose}>
      <div className="statistics-panel" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="statistics-header">
          <h3>📊 标注统计</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="statistics-body">
          {loading && (
            <div className="statistics-loading">
              <div className="spinner"></div>
              <p>加载统计数据中...</p>
            </div>
          )}

          {error && (
            <div className="statistics-error">
              <p>❌ {error}</p>
              <button onClick={fetchStatistics} className="retry-btn">重试</button>
            </div>
          )}

          {!loading && !error && statistics && (
            <>
              <div className="stats-overview">
                <div className="stat-card total">
                  <div className="stat-icon">📝</div>
                  <div className="stat-info">
                    <div className="stat-value">{statistics.total_annotations || 0}</div>
                    <div className="stat-label">总标注数</div>
                  </div>
                </div>

                <div className="stat-card pages">
                  <div className="stat-icon">📄</div>
                  <div className="stat-info">
                    <div className="stat-value">{statistics.annotated_pages || 0}</div>
                    <div className="stat-label">标注页面</div>
                  </div>
                </div>
              </div>

              <div className="stats-section">
                <h4>分类统计</h4>

                <div className="stat-item">
                  <div className="stat-item-header">
                    <span className="stat-item-label">
                      <span className="icon">📚</span> 导读记录
                    </span>
                    <span className="stat-item-value">{statistics.guide_count || 0}</span>
                  </div>
                  <div className="stat-progress-bar">
                    <div
                      className="stat-progress-fill guide"
                      style={{ width: `${getPercentage(statistics.guide_count, statistics.total_annotations)}%` }}
                    ></div>
                  </div>
                  <div className="stat-item-percentage">
                    {getPercentage(statistics.guide_count, statistics.total_annotations)}%
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-item-header">
                    <span className="stat-item-label">
                      <span className="icon">💬</span> 问答记录
                    </span>
                    <span className="stat-item-value">{statistics.qa_count || 0}</span>
                  </div>
                  <div className="stat-progress-bar">
                    <div
                      className="stat-progress-fill qa"
                      style={{ width: `${getPercentage(statistics.qa_count, statistics.total_annotations)}%` }}
                    ></div>
                  </div>
                  <div className="stat-item-percentage">
                    {getPercentage(statistics.qa_count, statistics.total_annotations)}%
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-item-header">
                    <span className="stat-item-label">
                      <span className="icon">📝</span> 手动标注
                    </span>
                    <span className="stat-item-value">{statistics.manual_count || 0}</span>
                  </div>
                  <div className="stat-progress-bar">
                    <div
                      className="stat-progress-fill manual"
                      style={{ width: `${getPercentage(statistics.manual_count, statistics.total_annotations)}%` }}
                    ></div>
                  </div>
                  <div className="stat-item-percentage">
                    {getPercentage(statistics.manual_count, statistics.total_annotations)}%
                  </div>
                </div>

                {statistics.deleted_count > 0 && (
                  <div className="stat-item">
                    <div className="stat-item-header">
                      <span className="stat-item-label">
                        <span className="icon">🗑️</span> 已删除
                      </span>
                      <span className="stat-item-value">{statistics.deleted_count}</span>
                    </div>
                    <div className="stat-progress-bar">
                      <div
                        className="stat-progress-fill deleted"
                        style={{ width: `${getPercentage(statistics.deleted_count, statistics.total_annotations + statistics.deleted_count)}%` }}
                      ></div>
                    </div>
                    <div className="stat-item-percentage">
                      {getPercentage(statistics.deleted_count, statistics.total_annotations + statistics.deleted_count)}%
                    </div>
                  </div>
                )}
              </div>

              <div className="stats-section">
                <h4>时间信息</h4>
                <div className="stats-time-info">
                  <div className="time-item">
                    <span className="time-label">最后标注：</span>
                    <span className="time-value">{formatDate(statistics.last_annotation_time)}</span>
                  </div>
                </div>
              </div>

              {statistics.total_annotations > 0 && (
                <div className="stats-section stats-tips">
                  <h4>💡 学习建议</h4>
                  {statistics.guide_count === 0 && (
                    <p className="tip">尝试使用"快速浏览"模式来系统性地理解文档内容。</p>
                  )}
                  {statistics.annotated_pages < 5 && statistics.total_annotations < 10 && (
                    <p className="tip">继续深入阅读，建立更完整的知识体系。</p>
                  )}
                  {statistics.total_annotations >= 20 && (
                    <p className="tip">标注数量丰富！可以生成批判性总结来梳理整体理解。</p>
                  )}
                  {statistics.deleted_count > statistics.total_annotations * 0.3 && (
                    <p className="tip warning">删除的标注较多，可以在"已删除"标签页中恢复需要的内容。</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="statistics-footer">
          <button onClick={onClose} className="done-btn">完成</button>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPanel;
